
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { recalculateScheduleWithInterestRate, createScheduleChangeEvent } from './utils/repaymentUtils';
import { AlertIcon } from '../BorrowingsIcons';

interface InterestRateChangeProps {
  loan: any;
  onSave: (updatedRepayments: any[], event: any) => void;
  onCancel: () => void;
}

export const InterestRateChange: React.FC<InterestRateChangeProps> = ({ 
  loan, 
  onSave, 
  onCancel 
}) => {
  const [newRate, setNewRate] = useState<string>(loan.interestRate.toString());
  const [benchmarkName, setBenchmarkName] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const parsedRate = parseFloat(newRate);
    if (isNaN(parsedRate) || parsedRate <= 0) {
      toast.error("Please enter a valid interest rate");
      return;
    }
    
    try {
      // Calculate new repayment schedule based on new interest rate
      const updatedRepayments = recalculateScheduleWithInterestRate(loan, parsedRate);
      
      // Create event record for this change
      const event = createScheduleChangeEvent(loan, 'interest_rate', {
        previousRate: loan.interestRate,
        newRate: parsedRate,
        benchmarkName: benchmarkName || "Not specified",
        reason,
        effectiveDate: new Date().toISOString().split('T')[0]
      });
      
      onSave(updatedRepayments, event);
    } catch (error) {
      toast.error("Failed to update repayment schedule");
    }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertIcon className="h-5 w-5" />
            Interest Rate Change
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Current Rate</label>
              <Input 
                value={`${loan.interestRate}%`} 
                disabled 
                className="bg-muted mt-1" 
              />
            </div>
            <div>
              <label className="text-sm font-medium">New Rate (%)</label>
              <Input 
                type="number" 
                step="0.01" 
                value={newRate} 
                onChange={(e) => setNewRate(e.target.value)}
                className="mt-1"
                min="0.01"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium">Benchmark (if applicable)</label>
            <Input
              placeholder="e.g., MIBOR, SOFR, Base Rate" 
              value={benchmarkName}
              onChange={(e) => setBenchmarkName(e.target.value)}
              className="mt-1"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium">Reason for Change</label>
            <Input
              placeholder="e.g., Market rate change, Contract renegotiation" 
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1"
              required
            />
          </div>
          
          <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertIcon className="h-5 w-5 text-amber-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">Important Note</h3>
                <div className="mt-2 text-sm text-amber-700">
                  <p>
                    This will recalculate all unpaid repayments based on the new interest rate.
                    The changes will need to be reviewed and approved before taking effect.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="border-t bg-muted/50 flex justify-end gap-2 pt-3 pb-3">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">
            Calculate New Schedule
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
};
